/**
 * OGP Render
 *
 * Renders fetched Open Graph preview cards and related warning states.
 */

import { UrlExtractor } from "../url/url-extractor";
import { z } from "zod";
import { FetchOgp200Response, FetchOgpImage200Response } from "../../api/generated/ogp.zod";
import { OgpApi } from "./ogp-api";
import { isOgpFetchSuppressed } from "./ogp-fetch-suppression";
import { Roles } from "../../constants/roles";
import commonUtil, { CommonUtils } from "../common-utils";
import { ogpPreviewForm } from "./ogp-preview-form";

type OgpRenderMode = "full" | "compact" | "card";

interface OgpLimitWarningOptions {
    totalUrls: number;
    maxUrls: number;
    message: string;
}

type OgpFetchResponseBody = z.infer<typeof FetchOgp200Response>;
type OgpResponseData = NonNullable<OgpFetchResponseBody["data"]>;
type OgpFetchImageResponseBody = z.infer<typeof FetchOgpImage200Response>;

interface ProcessDetailOptions {
    maxUrls?: number;
    renderMode?: OgpRenderMode;
}

function normalizeOgpData(value: unknown | OgpResponseData): OgpResponseData {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }

    const source = value as Partial<OgpResponseData> & Record<string, unknown>;
    const next: OgpResponseData = {};
    const keys: Array<keyof OgpResponseData> = ["url", "ogp_title", "general_description", "site_name", "image"];
    keys.forEach((key) => {
        const current = source[key];
        if (typeof current === "string") {
            next[key] = current;
        }
    });
    return next;
}

function hasRenderableOgpContent(ogpData: OgpResponseData | null | undefined): boolean {
    if (!ogpData) {
        return false;
    }

    return [ogpData.ogp_title, ogpData.general_description, ogpData.site_name, ogpData.image].some(
        (value) => typeof value === "string" && value.trim() !== "",
    );
}

function createExcludePatterns(): RegExp[] {
    return [
        /youtube\.com|youtu\.be/i,
        /facebook\.com|fb\.com/i,
        /instagram\.com/i,
        /twitter\.com|x\.com/i,
        /tiktok\.com/i,
    ];
}

function isImageLikePath(pathname: string): boolean {
    return /\.(avif|gif|jpe?g|png|svg|webp|bmp)(?:$|[?#])/i.test(pathname);
}

function isLocalDevelopmentHost(url: URL): boolean {
    const hostname = url.hostname.toLowerCase();

    return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local");
}

function isBlockedLocalPreviewUrl(url: URL): boolean {
    return url.pathname.startsWith("/media/") || (isLocalDevelopmentHost(url) && isImageLikePath(url.pathname));
}

function isMentionUrl(url: URL): boolean {
    return /^\/users\/\d+/.test(url.pathname);
}

// A link back to this same site is never a useful "OGP preview" target: the
// backend has to fetch the URL server-side to scrape its meta tags, and
// fetching the app's own page from within its own container can hang
// indefinitely (no loopback route back to itself) rather than fail fast —
// exactly the "spinner never resolves" symptom this guards against. Mirrors
// isMentionUrl()/isInternalHashtagSearchUrl() below, which already special-case
// other internal-link shapes for the same underlying reason.
function isSameOriginUrl(url: URL): boolean {
    try {
        return url.origin === window.location.origin;
    } catch {
        return false;
    }
}

function isInternalHashtagSearchUrl(url: URL): boolean {
    const searchValue = String(url.searchParams.get("search") || "").trim();
    if (!searchValue.startsWith("#")) {
        return false;
    }

    const normalizedPath = url.pathname.replace(/\/+$/, "");
    return /\/posts\/[^/]+$/i.test(normalizedPath);
}

export function renderOgpLimitWarning(
    container: HTMLElement,
    { totalUrls, maxUrls, message }: OgpLimitWarningOptions,
): void {
    if (!(container instanceof HTMLElement)) return;

    ogpPreviewForm.getLimitWarning(container)?.remove();
    const safeMessage = commonUtil.escapeHtml(String(message || ""));
    const safeTotal = commonUtil.escapeHtmlAttribute(String(totalUrls));
    const safeMax = commonUtil.escapeHtmlAttribute(String(maxUrls));

    container.insertAdjacentHTML(
        "beforeend",
        `<div class="ogp-limit-warning" data-translate="ogp_preview_limit_warning" data-total-urls="${safeTotal}" data-max-urls="${safeMax}">${safeMessage}</div>`,
    );
}

class OgpRender {
    private static sharedCache: Map<string, OgpResponseData> = new Map();

    private static inFlightRequests: Map<string, Promise<OgpResponseData | null>> = new Map();

    private static failedUntilByUrl: Map<string, number> = new Map();

    private static readonly failureRetryDelayMs = 30000;

    public cache: Map<string, OgpResponseData>;

    private api: OgpApi;

    public constructor() {
        this.cache = OgpRender.sharedCache;
        this.api = new OgpApi();
    }

    private static getFailureCooldown(url: string): boolean {
        const failedUntil = this.failedUntilByUrl.get(url) || 0;
        return failedUntil > Date.now();
    }

    private static markFailure(url: string): void {
        this.failedUntilByUrl.set(url, Date.now() + this.failureRetryDelayMs);
    }

    private async fetchOgpData(url: string): Promise<OgpResponseData | null> {
        if (!url) return null;

        if (this.cache.has(url)) {
            return this.cache.get(url) || null;
        }

        if (OgpRender.getFailureCooldown(url)) {
            return null;
        }

        const inFlight = OgpRender.inFlightRequests.get(url);
        if (inFlight) {
            return inFlight;
        }

        const request = (async () => {
            try {
                const response = await this.api.fetchOgp(url);
                if (!response.ok) {
                    OgpRender.markFailure(url);
                    console.warn("OGP fetch failed:", url, response.status);
                    return null;
                }

                const rawResult = (await response.json()) as OgpFetchResponseBody;
                if (!(rawResult.success && rawResult.data)) {
                    OgpRender.markFailure(url);
                    return null;
                }

                const ogpData = normalizeOgpData(rawResult.data);
                this.cache.set(url, ogpData);
                OgpRender.failedUntilByUrl.delete(url);
                return ogpData;
            } catch (error) {
                OgpRender.markFailure(url);
                console.error("OGP fetch error:", error);
                return null;
            } finally {
                OgpRender.inFlightRequests.delete(url);
            }
        })();

        OgpRender.inFlightRequests.set(url, request);
        return request;
    }

    public static isFetchSuppressed(): boolean {
        try {
            return isOgpFetchSuppressed();
        } catch (_error) {
            return false;
        }
    }

    public isFetchSuppressed(): boolean {
        return OgpRender.isFetchSuppressed();
    }

    private isExcludedFromOgpScan(el: Element | null | undefined): boolean {
        return !!(ogpPreviewForm.getContentElementSnsAncestor(el) || ogpPreviewForm.getContentElementProductEmbedAncestor(el));
    }

    public removeExistingPreview(targetElement: HTMLElement, url: string): void {
        if (!targetElement || !url) return;
        const children = Array.from(targetElement.children);
        for (const child of children) {
            if (!(child instanceof HTMLElement)) continue;
            if (child.getAttribute("data-ogp-url") === url) {
                child.remove();
            }
        }
    }

    public detectUrls(content: string): string[] {
        if (!content) return [];
        return this.filterEligibleUrls(UrlExtractor.detectUrls(this.stripProductEmbedMarkup(content), { excludeSNS: true }));
    }

    /**
     * detectUrls() scans post content as a raw HTML string via regex (no DOM
     * context), so it can't tell a user-typed/pasted link from the several
     * real <a href> links inside an embedded product-linker card (see
     * wysiwyg-product-embed/) — e.g. /products/{id}, /brands/{id} all pass
     * isEligibleUrl(). Parse-and-strip .ql-product-embed blocks first so those
     * links never reach the regex scan. Skipped when the marker string isn't
     * present, since this runs on every content-change debounce.
     */
    private stripProductEmbedMarkup(content: string): string {
        if (!content.includes("ql-product-embed")) return content;

        try {
            const wrapper = document.createElement("div");
            wrapper.innerHTML = content;
            ogpPreviewForm.getProductEmbedElements(wrapper).forEach((el) => el.remove());
            return wrapper.innerHTML;
        } catch (_error) {
            return content;
        }
    }

    public filterExistingPlatforms(urls: string[]): string[] {
        const excludePatterns = createExcludePatterns();
        return urls.filter((url) => !excludePatterns.some((pattern) => pattern.test(url)));
    }

    public isEligibleUrl(url: string): boolean {
        const trimmedUrl = String(url || "").trim();
        if (!trimmedUrl) {
            return false;
        }

        try {
            const parsedUrl = new URL(trimmedUrl);
            const protocol = parsedUrl.protocol.toLowerCase();
            if (protocol !== "http:" && protocol !== "https:") {
                return false;
            }

            if (this.filterExistingPlatforms([trimmedUrl]).length === 0) {
                return false;
            }

            if (isBlockedLocalPreviewUrl(parsedUrl)) {
                return false;
            }

            if (isSameOriginUrl(parsedUrl)) {
                return false;
            }

            if (isMentionUrl(parsedUrl)) {
                return false;
            }

            if (isInternalHashtagSearchUrl(parsedUrl)) {
                return false;
            }

            if (isImageLikePath(parsedUrl.pathname)) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    public filterEligibleUrls(urls: string[]): string[] {
        return Array.from(new Set(urls.map((url) => String(url || "").trim()))).filter((url) =>
            this.isEligibleUrl(url),
        );
    }

    public async fetchAndRender(
        url: string,
        targetElement: HTMLElement,
        renderMode: OgpRenderMode = "full",
    ): Promise<void> {
        if (!url || !targetElement) {
            console.warn("OgpRender: Missing url or targetElement", { url, targetElement });
            return;
        }

        if (!this.isEligibleUrl(url)) {
            return;
        }

        if (OgpRender.isFetchSuppressed()) {
            const inElementModal = ogpPreviewForm.isInsideElementModal(targetElement);
            if (!inElementModal) return;
        }

        if (this.cache.has(url)) {
            const cachedData = this.cache.get(url) || {};
            if (!hasRenderableOgpContent(cachedData)) {
                return;
            }
            const preview = this.renderByMode(cachedData, renderMode);
            const resolvedUrl = cachedData.url || url;
            this.removeExistingPreview(targetElement, resolvedUrl);
            targetElement.appendChild(preview);
            return;
        }

        try {
            const ogpData = await this.fetchOgpData(url);
            if (!hasRenderableOgpContent(ogpData)) {
                return;
            }

            const preview = this.renderByMode(ogpData, renderMode);
            const resolvedUrl = ogpData.url || url;
            this.removeExistingPreview(targetElement, resolvedUrl);
            targetElement.appendChild(preview);
        } catch (error) {
            console.error("OGP fetch error:", error);
        }
    }

    public async fetchLocalOgpImagePath(url: string): Promise<string | null> {
        try {
            const response = await this.api.fetchOgpImage(url);
            if (!response.ok) return null;

            const result = (await response.json()) as OgpFetchImageResponseBody;
            if (!result || !result.success) return null;

            const rawPath = typeof result.image_path === "string" ? result.image_path : "";
            const cleaned = String(rawPath || "").trim();
            if (!cleaned) return null;

            const normalized = cleaned.replace(/^\/+/, "");
            return CommonUtils.resolveUrl(`/${normalized}`);
        } catch (_error) {
            return null;
        }
    }

    public async fetchPreviewImageUrl(_url: string): Promise<string | null> {
        // CSR OGP image fetch disabled: SSR renders previews from attached_images (ogp_* files)
        return null;
        // const localImagePath = await this.fetchLocalOgpImagePath(_url);
        // if (localImagePath) {
        //     return localImagePath;
        // }
        // const ogpData = await this.fetchOgpData(_url);
        // const imageUrl = typeof ogpData?.image === "string" ? String(ogpData.image).trim() : "";
        // return imageUrl || null;
    }

    public renderPreview(ogpData: OgpResponseData): HTMLDivElement {
        const container = document.createElement("div");
        container.className = "ogp-preview";
        container.setAttribute("data-ogp-url", ogpData.url || "");

        const link = document.createElement("a");
        link.href = ogpData.url || "#";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.className = "ogp-link";

        if (ogpData.image) {
            const imageDiv = document.createElement("div");
            imageDiv.className = "ogp-image";

            const img = document.createElement("img");
            img.src = ogpData.image;
            img.alt = ogpData.ogp_title || "Preview";
            img.loading = "lazy";
            img.onerror = () => {
                imageDiv.style.display = "none";
            };

            imageDiv.appendChild(img);
            link.appendChild(imageDiv);
        }

        const contentDiv = document.createElement("div");
        contentDiv.className = "ogp-content";

        if (ogpData.ogp_title) {
            const titleDiv = document.createElement("div");
            titleDiv.className = "ogp-title";
            titleDiv.textContent = commonUtil.decodeHtmlEntities(ogpData.ogp_title);
            contentDiv.appendChild(titleDiv);
        }

        if (ogpData.general_description) {
            const descDiv = document.createElement("div");
            descDiv.className = "ogp-description";
            descDiv.textContent = commonUtil.decodeHtmlEntities(ogpData.general_description);
            contentDiv.appendChild(descDiv);
        }

        if (ogpData.site_name) {
            const siteDiv = document.createElement("div");
            siteDiv.className = "ogp-site-name";
            siteDiv.textContent = commonUtil.decodeHtmlEntities(ogpData.site_name);
            contentDiv.appendChild(siteDiv);
        }

        link.appendChild(contentDiv);
        container.appendChild(link);
        return container;
    }

    public renderCompactPreview(ogpData: OgpResponseData): HTMLDivElement {
        const container = document.createElement("div");
        container.className = "ogp-preview ogp-preview-compact";
        container.setAttribute("data-ogp-url", ogpData.url || "");

        const link = document.createElement("a");
        link.href = ogpData.url || "#";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.className = "ogp-link";

        if (ogpData.image) {
            const imageDiv = document.createElement("div");
            imageDiv.className = "ogp-image";

            const img = document.createElement("img");
            img.src = ogpData.image;
            img.alt = ogpData.ogp_title || "Preview";
            img.loading = "lazy";
            img.onerror = () => {
                imageDiv.style.display = "none";
            };

            imageDiv.appendChild(img);
            link.appendChild(imageDiv);
        }

        const contentDiv = document.createElement("div");
        contentDiv.className = "ogp-content";

        if (ogpData.ogp_title) {
            const titleDiv = document.createElement("div");
            titleDiv.className = "ogp-title";
            titleDiv.textContent = commonUtil.decodeHtmlEntities(ogpData.ogp_title);
            contentDiv.appendChild(titleDiv);
        }

        if (ogpData.site_name) {
            const siteDiv = document.createElement("div");
            siteDiv.className = "ogp-site-name";
            siteDiv.textContent = commonUtil.decodeHtmlEntities(ogpData.site_name);
            contentDiv.appendChild(siteDiv);
        }

        link.appendChild(contentDiv);
        container.appendChild(link);
        return container;
    }

    public renderCardPreview(ogpData: OgpResponseData): HTMLDivElement {
        const container = document.createElement("div");
        container.className = "ogp-preview-card";
        container.setAttribute("data-role", Roles.OGP_PREVIEW_CARD);
        container.setAttribute("data-ogp-url", ogpData.url || "");

        const content = document.createElement("div");
        content.className = "ogp-preview-card__content";

        if (ogpData.image) {
            const img = document.createElement("img");
            img.className = "ogp-preview-card__image";
            img.src = ogpData.image;
            img.alt = ogpData.ogp_title || "Preview";
            img.loading = "lazy";

            let triedLocalFallback = false;

            img.onerror = () => {
                if (!triedLocalFallback) {
                    triedLocalFallback = true;
                    void (async () => {
                        const localSrc = await this.fetchLocalOgpImagePath(ogpData.url || "");
                        if (localSrc) {
                            img.src = localSrc;
                            return;
                        }

                        img.style.display = "none";
                        const placeholder = this.createCardPlaceholder();
                        content.appendChild(placeholder);
                    })();
                    return;
                }

                img.style.display = "none";
                const placeholder = this.createCardPlaceholder();
                content.appendChild(placeholder);
            };

            content.appendChild(img);
        } else {
            const placeholder = this.createCardPlaceholder();
            content.appendChild(placeholder);
        }

        container.appendChild(content);
        return container;
    }

    public async processCard(cardElement: HTMLElement): Promise<void> {
        if (!cardElement) return;

        if (OgpRender.isFetchSuppressed()) {
            const inElementModal = ogpPreviewForm.isInsideElementModal(cardElement);
            if (!inElementModal) return;
        }

        if (cardElement.hasAttribute("data-ogp-processed")) {
            return;
        }

        const contentElement = ogpPreviewForm.getCardContent(cardElement);
        if (!(contentElement instanceof HTMLElement)) return;

        const content = contentElement.innerHTML || contentElement.textContent || contentElement.innerText;
        const urls = this.detectUrls(content);
        if (urls.length === 0) return;

        const mediaArea = ogpPreviewForm.getBoardMediaArea(cardElement);
        if (!(mediaArea instanceof HTMLElement)) return;

        await this.fetchAndRender(urls[0], mediaArea);
        cardElement.setAttribute("data-ogp-processed", "true");
    }

    public async processDetailContent(contentElement: HTMLElement, options: ProcessDetailOptions = {}): Promise<void> {
        if (!contentElement) return;

        if (OgpRender.isFetchSuppressed()) {
            const inElementModal = ogpPreviewForm.isInsideElementModal(contentElement);
            if (!inElementModal) return;
        }

        const hrefCandidates = ogpPreviewForm
            .getHrefLinks(contentElement)
            .filter((anchor) => !(anchor instanceof HTMLElement) || !this.isExcludedFromOgpScan(anchor))
            .map((anchor) => {
                try {
                    return anchor instanceof HTMLAnchorElement ? anchor.href || anchor.getAttribute("href") || "" : "";
                } catch (_error) {
                    return anchor.getAttribute("href") || "";
                }
            })
            .filter((value): value is string => typeof value === "string" && value.trim() !== "");

        const textUrls = this.detectUrlsFromTextNodes(contentElement);
        const hrefUrls = hrefCandidates.flatMap((href) => this.detectUrls(href));
        const urls = this.filterEligibleUrls([...hrefUrls, ...textUrls]);

        const maxUrls = options.maxUrls || 1;
        const urlsToProcess = urls.slice(0, maxUrls);

        if (!OgpRender.isFetchSuppressed()) {
            this.removeTextUrls(contentElement, urlsToProcess);
        }

        for (const url of urlsToProcess) {
            await this.fetchAndRender(url, contentElement);
        }
    }

    private detectUrlsFromTextNodes(contentElement: HTMLElement): string[] {
        const walker = document.createTreeWalker(contentElement, NodeFilter.SHOW_TEXT);
        const urls: string[] = [];

        let node = walker.nextNode();
        while (node) {
            if (node instanceof Text) {
                const parent = node.parentElement;
                const tag = parent?.tagName?.toLowerCase() || "";

                if (
                    tag !== "a" &&
                    tag !== "script" &&
                    tag !== "style" &&
                    tag !== "textarea" &&
                    !(parent instanceof HTMLElement && this.isExcludedFromOgpScan(parent))
                ) {
                    urls.push(...this.detectUrls(node.nodeValue || ""));
                }
            }

            node = walker.nextNode();
        }

        return urls;
    }

    public removeTextUrls(element: HTMLElement, urls: string[]): void {
        try {
            if (!element || !Array.isArray(urls) || urls.length === 0) return;

            const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
            const textNodes: Text[] = [];

            let node = walker.nextNode();
            while (node) {
                if (node.nodeType === Node.TEXT_NODE) {
                    const parent = node.parentElement;
                    const tag = parent?.tagName?.toLowerCase() || "";
                    if (tag !== "a" && tag !== "script" && tag !== "style" && tag !== "textarea") {
                        if (node instanceof Text) {
                            textNodes.push(node);
                        }
                    }
                }
                node = walker.nextNode();
            }

            if (textNodes.length === 0) return;

            const escapedUrls = urls
                .filter((url) => typeof url === "string" && url.trim() !== "")
                .map((url) => url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
            if (escapedUrls.length === 0) return;

            const urlRegex = new RegExp(`(${escapedUrls.join("|")})`, "gi");

            for (const textNode of textNodes) {
                const original = textNode.nodeValue || "";
                const replaced = original.replace(urlRegex, "");
                if (replaced !== original) {
                    textNode.nodeValue = replaced;
                }
            }
        } catch (error) {
            console.error("Failed to remove text URLs", error);
        }
    }

    public clearCache(): void {
        this.cache.clear();
        OgpRender.failedUntilByUrl.clear();
        OgpRender.inFlightRequests.clear();
    }

    private renderByMode(ogpData: OgpResponseData, renderMode: OgpRenderMode): HTMLDivElement {
        switch (renderMode) {
            case "card":
                return this.renderCardPreview(ogpData);
            case "compact":
                return this.renderCompactPreview(ogpData);
            case "full":
            default:
                return this.renderPreview(ogpData);
        }
    }

    private createCardPlaceholder(): HTMLDivElement {
        const placeholder = document.createElement("div");
        placeholder.style.cssText = `
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            font-size: 32px;
        `;
        placeholder.textContent = "🔗";
        return placeholder;
    }
}

export { OgpRender };
